import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import HeaderOne from '../Components/Header/HeaderOne';
import FooterOne from '../Components/Footer/FooterOne';
import '../styles/car-hire-details.css';

const CarHireDetails = () => {
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageError, setImageError] = useState(false);

  const fetchCarDetails = async () => {
    try {
      const response = await fetch(`https://tours.dzsoftech.net/api/car-hire/cars/${id}/`);
      if (!response.ok) {
        throw new Error('Car not found');
      }
      const data = await response.json();
      setCar(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCarDetails();
  }, [id]);

  const handleImageError = (e) => {
    if (!imageError) {
      setImageError(true);
      // Try a different fallback image or hide the image
      e.target.style.display = 'none';
    }
  };

  if (loading) {
    return (
      <>
        <HeaderOne />
        <div className="car-details">
          <div className="container">
            <div className="loading-spinner">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="mt-3">Loading car details...</p>
            </div>
          </div>
        </div>
        <FooterOne />
      </>
    );
  };

  if (error || !car) {
    return (
      <>
        <HeaderOne />
        <div className="car-details">
          <div className="container">
            <div className="error-message">
              <h3>Car Not Found</h3>
              <p>Sorry, we couldn't find the car you're looking for.</p>
              <a href="/home-carhire" className="btn btn-primary">Browse Other Cars</a>
            </div>
          </div>
        </div>
        <FooterOne />
      </>
    );
  }

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(<i key={i} className="fas fa-star text-warning"></i>);
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(<i key={i} className="fas fa-star-half-alt text-warning"></i>);
      } else {
        stars.push(<i key={i} className="fas fa-star text-muted"></i>);
      }
    }
    return stars;
  };

  return (
    <>
      <HeaderOne />
      <section className="car-details">
        <div className="container">
          <div className="car-details-container">
            {/* Header Section */}
            <div className="car-details-header">
              <div className="car-company">{car.company?.name || 'Car Rental'}</div>
              <h2>{car.make} {car.model} {car.year}</h2>
              <div className="car-rating">
                <div className="rating">
                  {renderStars(car.rating || 4.5)}
                </div>
                <span className="reviews-count">({car.reviews_count || 0} reviews)</span>
              </div>
            </div>

            {/* Content Section */}
            <div className="car-details-content">
              <div className="car-info">
                {/* Car Image */}
                <div className="car-image-section">
                  {!imageError ? (
                    <img 
                      src={car.image_url || '/assets/images/cars/default-car.jpg'} 
                      alt={`${car.make} ${car.model}`}
                      className="car-image"
                      onError={handleImageError}
                    />
                  ) : (
                    <div className="car-image-placeholder">
                      <div className="placeholder-content">
                        <i className="fas fa-car fa-4x text-muted"></i>
                        <p className="mt-3">Car Image Not Available</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Car Features */}
                <div className="car-features">
                  <h4>Key Features</h4>
                  <div className="features-grid">
                    <div className="feature-item">
                      <i className="fas fa-users feature-icon"></i>
                      <span className="feature-text">{car.seats || 4} Seats</span>
                    </div>
                    <div className="feature-item">
                      <i className="fas fa-suitcase feature-icon"></i>
                      <span className="feature-text">{car.bags || 2} Bags</span>
                    </div>
                    <div className="feature-item">
                      <i className="fas fa-cog feature-icon"></i>
                      <span className="feature-text">{car.transmission || 'Automatic'}</span>
                    </div>
                    <div className="feature-item">
                      <i className="fas fa-gas-pump feature-icon"></i>
                      <span className="feature-text">{car.fuel_type || 'Petrol'}</span>
                    </div>
                    <div className="feature-item">
                      <i className="fas fa-car feature-icon"></i>
                      <span className="feature-text">{car.category || 'Compact'}</span>
                    </div>
                    <div className="feature-item">
                      <i className="fas fa-snowflake feature-icon"></i>
                      <span className="feature-text">Air Conditioning</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Booking Sidebar */}
              <div className="car-booking-sidebar">
                <div className="price-box">
                  <div className="price-label">Starting from</div>
                  <div className="price">
                    <span className="amount">${car.price_per_day}</span>
                    <span className="period">/day</span>
                  </div>
                </div>

                <div className="booking-actions">
                  <button className="btn-book-now">
                    <i className="fas fa-calendar-check me-2"></i>
                    Book Now
                  </button>
                  <button className="btn-add-to-wishlist">
                    <i className="fas fa-heart me-2"></i>
                    Add to Wishlist
                  </button>

                  <div className="booking-info">
                    <div className="info-item">
                      <i className="fas fa-check info-icon"></i>
                      <span>Free Cancellation</span>
                    </div>
                    <div className="info-item">
                      <i className="fas fa-shield-alt info-icon"></i>
                      <span>Insurance Included</span>
                    </div>
                    <div className="info-item">
                      <i className="fas fa-clock info-icon"></i>
                      <span>Instant Confirmation</span>
                    </div>
                    <div className="info-item">
                      <i className="fas fa-medal info-icon"></i>
                      <span>Best Price Guarantee</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <FooterOne />
    </>
  );
};

export default CarHireDetails;
